import React from 'react';
import { useTranslation } from 'react-i18next';
import { findNodeHandle, StyleSheet, View } from 'react-native';
import axios from 'axios';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import {
  apiConfig,
  loadingPageTestIDs,
  onboardingTestIDs,
  settingsMenuTestIDs,
  settingsPageTestIDs,
  Theme,
} from '@constants';
import { useContextReference } from '@hooks';
import { navigationService } from '@services';
import { userAuthState, userSettingsState } from '@store';

import { getDeviceKey } from '../../../helpers/KeyUtils';
import { isAndroid, platformName } from '../../../helpers/Platform';
import { clearStorage } from '../../../helpers/storage';
import { getToken } from '../../../hooks/api/useGetAdobeTVEUserAuth';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { segmentEventTypes } from '../../segment/segmentEventTypes';
import { segmentTrackEvent } from '../../segment/SegmentTrackEvent';

const tPath = 'settingsPage.signOut';

export const SettingsSignOut = () => {
  const userSettings = useRecoilValue(userSettingsState);
  const resetUserSettings = useResetRecoilState(userSettingsState);
  const resetUserAuthState = useResetRecoilState(userAuthState);
  const deviceID = getDeviceKey();
  const { deviceInfoString, isCableSubscriber } = userSettings;
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const settingsSignOutButtonReference = useContextReference(settingsPageTestIDs.signOut.settingsSignOutButton);

  const settingsMenuSignOutReference = useContextReference(settingsMenuTestIDs.signOut);

  const nextFocusLeft = isAndroid
    ? findNodeHandle(settingsMenuSignOutReference.current)
    : settingsMenuSignOutReference.current;

  const finishSigningOut = () => {
    segmentTrackEvent(segmentEventTypes.LOGOUT, userSettings);
    clearStorage();
    resetUserSettings();
    resetUserAuthState();
    navigationService.replace(loadingPageTestIDs.loadingPage);
  };

  const signOut = async (retry = false) => {
    if (isCableSubscriber) {
      const token = await getToken(retry);

      return await axios
        .delete(`${apiConfig.ADOBE_TVE_LOGOUT_URL}?deviceId=${deviceID}&deviceType=${platformName()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Device-Info': deviceInfoString,
          },
        })
        .then(() => {
          finishSigningOut();
        })
        .catch(error => {
          // if we get a 401, the token has expired, so try again, which will force a new token in getToken
          if (error.response.status === 401) {
            console.log('adobe token expired getting a new one...');
            return retry ? { error: 'access error' } : signOut(true);
          } else {
            // we got another kind of error, so send the user to the error page
            // this error page is designed for the onboarding flow. we need to talk to product/design about what we should actualy do here
            // perhaps an inline message on the current page?
            navigationService.navigate(onboardingTestIDs.pages.errorPage);
          }
        });
    } else {
      finishSigningOut();
    }
  };

  return (
    <View style={styles.settingsSignOutWrapper}>
      <View style={styles.settingsSignOutTopContent}>
        <TWCText testID={settingsPageTestIDs.signOut.settingsSignOutText} variant={'regularText36'}>
          {t('signOutText')}
        </TWCText>
      </View>
      <TWCButton
        accessibilityHint={t('signOutAccessibilityHint')}
        accessibilityLabel={t('signOutAccessibilityLabel')}
        nativeID={settingsPageTestIDs.signOut.settingsSignOutButton}
        nextFocusLeft={nextFocusLeft}
        ref={settingsSignOutButtonReference}
        testID={settingsPageTestIDs.signOut.settingsSignOutButton}
        title={t('signOutButtonLabel')}
        onPress={() => signOut()}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  settingsSignOutTopContent: {
    paddingBottom: Theme.px(59),
  },
  settingsSignOutWrapper: {
    paddingTop: Theme.px(311),
  },
});
